import { type MouseEvent, useState } from 'react';
import ReactGA from 'react-ga4';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, Link, Menu, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { CONFIG } from '~/config';

function TopMenu() {
  const theme = useTheme();
  const location = useLocation();
  const [travelHubAnchorEl, setTravelHubAnchorEl] = useState<null | HTMLElement>(null);
  const isTravelHubOpen = Boolean(travelHubAnchorEl);
  const route = {
    home: '/',
    flightItinerary: '/itinerary',
  };
  const infoRoot = CONFIG.app.helpCenterLink.replace(/\/$/, '');
  const travelHubLinks = [
    { text: 'Flight Routes', href: `${infoRoot}/cheap-flights` },
    { text: 'Cities', href: `${infoRoot}/cheap-flights-from` },
    { text: 'Visa Itinerary Guides', href: `${infoRoot}/visa-itinerary/` },
    { text: 'Travel Blog', href: `${infoRoot}/blog/` },
    { text: 'Ask for Help', href: `${infoRoot}/ask-for-help/` },
  ];

  const isActive = (link: string): boolean => {
    if (link === route.flightItinerary) {
      return location.pathname.startsWith(route.flightItinerary);
    }

    if (link === route.home) {
      return location.pathname === route.home;
    }

    return location.pathname === link;
  };

  const trackMenuClick = (label: string, value: number) => {
    ReactGA.event({
      category: 'TopMenu',
      action: 'Menu Click',
      label,
      value,
    });
  };

  const handleTravelHubOpen = (event: MouseEvent<HTMLElement>) => {
    setTravelHubAnchorEl(event.currentTarget);
    trackMenuClick('Travel hub', 2);
  };

  const handleTravelHubHover = (event: MouseEvent<HTMLElement>) => {
    setTravelHubAnchorEl(event.currentTarget);
  };

  const handleTravelHubClose = () => {
    setTravelHubAnchorEl(null);
  };

  const menuLinkSx = (active = false) => ({
    color: active ? theme.palette.primary.main : theme.palette.secondary.main,
    fontWeight: 'bold',
    fontSize: 16,
    '&:hover': {
      color: active ? theme.palette.secondary.main : theme.palette.primary.main,
    },
    '&:after': {
      height: 2,
      display: 'block',
      width: '50%',
      background: active ? theme.palette.primary.main : 'none',
      margin: '5px auto 0',
      content: '" "',
      '&:hover': {
        background: theme.palette.secondary.main,
      },
    },
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center', gap: 4 }}>
      {['Search Flights', 'Request Itinerary'].map((text, index) => {
        const links = [route.home, route.flightItinerary];

        return (
          <Typography key={text} sx={{ flexGrow: 1 }} variant="body1">
            <Link
              component={RouterLink}
              onClick={() => {
                trackMenuClick(text, index);
              }}
              sx={menuLinkSx(isActive(links[index]))}
              to={links[index]}
              underline="none"
            >
              {text}
            </Link>
          </Typography>
        );
      })}
      <Typography sx={{ flexGrow: 1, whiteSpace: 'nowrap' }} variant="body1">
        <Button
          aria-controls={isTravelHubOpen ? 'travel-hub-menu' : undefined}
          aria-expanded={isTravelHubOpen ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleTravelHubOpen}
          onMouseEnter={handleTravelHubHover}
          sx={{
            ...menuLinkSx(isTravelHubOpen),
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            fontFamily: 'inherit',
            gap: 0.25,
            lineHeight: 1.5,
            p: 0,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
          type="button"
        >
          <Box component="span" sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Travel hub
          </Box>
          <ArrowDropDownIcon
            aria-hidden="true"
            fontSize="small"
            sx={{
              color: 'inherit',
              transition: 'transform .18s ease',
              transform: isTravelHubOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        </Button>
        <Menu
          MenuListProps={{ 'aria-label': 'Travel hub' }}
          anchorEl={travelHubAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          id="travel-hub-menu"
          onClose={handleTravelHubClose}
          open={isTravelHubOpen}
          slotProps={{
            paper: {
              onMouseEnter: () => {
                // Keep the existing button anchor while the user moves into the dropdown.
                // Re-anchoring to the menu paper causes the dropdown to jump.
              },
              onMouseLeave: handleTravelHubClose,
            },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {travelHubLinks.map((item, index) => (
            <MenuItem
              key={item.href}
              component="a"
              href={item.href}
              onClick={() => {
                handleTravelHubClose();
                trackMenuClick(item.text, index + 3);
              }}
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 'bold',
                fontSize: 15,
                minWidth: 180,
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {item.text}
            </MenuItem>
          ))}
        </Menu>
      </Typography>
    </Box>
  );
}

export default TopMenu;
